import React, {FC} from "react";
import {useAxios} from "../../utils/hooks";
import useSafeState from "../../utils/safe-state";
import {Trans} from "@lingui/macro";
import {Breadcrumb, Button, Divider, Popconfirm, Tooltip, Upload, UploadProps} from "antd";
import {
    DeleteTwoTone,
    PlusOutlined,
    UploadOutlined
} from "@ant-design/icons/lib";
import DtoTable from "../../components/dto-table/dto-table";
import ButtonWithIcon from "../../components/buttons/button-with-icon";
import DdsAssignmentRuleModel from "../../models/ddsassignmentrules/dds-assignment-rule-model";
import Drawer from "../../components/drawer/drawer";
import DdsAssignmentRuleDrawer from "../../forms/dds-assignment-rule-form";
import {DownloadOutlined, EditOutlined} from "@ant-design/icons";
import {openNotification} from "../../utils/openNotification";
import FileDownload from "js-file-download";


const DdsAssignmentRulesScreen: FC = () => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const [trigger, setTrigger] = useSafeState(false);
    const ddsAssignmentRuleText = <Trans>Dds Assignment Rule</Trans>;
    const [ddsLoading, setDdsLoading] = useSafeState(false);

    const ddsAssignmentRuleDrawer = Drawer(
        {
            title: ddsAssignmentRuleText,
            children:
                <DdsAssignmentRuleDrawer/>,
            customClose: () => setTrigger(!trigger)
        }
    );
    
    const handleDelete = (record: any) => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .delete('/resource/dds-assignment-rules/' + encodeURIComponent(record.id))
                .then(() => {
                    openNotification('customSuccess', 'Successfully deleted.');
                    setTrigger(!trigger);
                })
                .catch(function (error: any) {
                    openNotification('error', error);
                });
        }
    };

    const action = (record: any) => {
        return (
            <>
                <Tooltip title={<Trans>Edit</Trans>}>
                    <EditOutlined className={"ActionButtons"} style={{color: "#40a9ff"}}
                                  onClick={() =>
                                      ddsAssignmentRuleDrawer
                                          .open(
                                              "Edit",
                                              {
                                                  id: record.id,
                                                  itemNo: record.item && record.item.itemNo,
                                                  ddsNumber: record.ddsNumber,
                                                  quantity: record.quantity,
                                                  validFrom: record.validFrom,
                                                  validTo: record.validTo,
                                                  version: record.version
                                              })
                                  }
                    />
                </Tooltip>
                <Tooltip title={<Trans>Remove</Trans>}>
                    <Popconfirm
                        title={<Trans>Are you sure want to delete record?</Trans>}
                        onConfirm={() => handleDelete(record)}
                        okText={<Trans>Yes</Trans>}
                        cancelText={<Trans>No</Trans>}>
                        <DeleteTwoTone className={"ActionButtons"} twoToneColor={"red"}/>
                    </Popconfirm>
                </Tooltip>
            </>
        )
    };

    const actions = {
        width: 100,
        render: (text: string, record: any) => action(record),
        title: '',
        fixed: 'right',
        align: 'center'
    };

    const handleDownloadXls = () => {
        if(axiosInstance.current != null){
            const FileDownload = require('js-file-download');
            axiosInstance.current({
                url: process.env.REACT_APP_API_BASE_URL + '/resource/dds-assignment-rules/download-template',
                method: 'GET',
                responseType: 'blob'
            }).then((response) => {
                FileDownload(response.data, 'dds_upload_template.xlsx');
            }).catch(function (error: any) {
                console.log(error);
            });
        }
    }

    const ddsProps: UploadProps = {
        name: 'file',
        multiple: false,
        showUploadList: false,
        customRequest: async (options) => {
            const { file, onSuccess, onError } = options;
            if (!axiosInstance.current) return;

            setDdsLoading(true);
            const formData = new FormData();
            formData.append('file', file as any);

            try {
                await axiosInstance.current.post('/resource/dds-assignment-rules/upload-xls', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                openNotification('customSuccess', 'File successfully uploaded');
                onSuccess && onSuccess({}, file as any);
            } catch (error) {
                openNotification('error', error);
                onError && onError(error as any);
            } finally {
                setDdsLoading(false);
            }
        },
        onChange(info) {
            if (info.file.status === 'done') {
                setTrigger(!trigger);
            }
        }
    };

    const header = (
        <>
            <Button type={'primary'} onClick={() => ddsAssignmentRuleDrawer.open('Add new')}>
                <ButtonWithIcon icon={<PlusOutlined />} text={<Trans>Add new</Trans>} />
            </Button>
            <Divider type="vertical" style={{ fontSize: 'xxx-large' }} />
            <Button onClick={() => handleDownloadXls()}>
                <ButtonWithIcon
                    icon={<DownloadOutlined />}
                    text={<Trans>.xlsx template</Trans>}
                />
            </Button>
            <Divider type="vertical" style={{ fontSize: 'xxx-large' }} />
            <Upload {...ddsProps}>
                <Button loading={ddsLoading}>
                    <ButtonWithIcon icon={<UploadOutlined/>} text={<Trans>.xlsx upload</Trans>} />
                </Button>
            </Upload>
            <Divider type="vertical" style={{ fontSize: 'xxx-large' }} />
        </>
    );

    return (<>
            <>
                <Breadcrumb style={{marginBottom: 10}}>
                    <Breadcrumb.Item><Trans id={'Home'}/></Breadcrumb.Item>
                    <Breadcrumb.Item><Trans id={'Master Data'}/></Breadcrumb.Item>
                    <Breadcrumb.Item><Trans id={'DDS Assignment Rules'}></Trans></Breadcrumb.Item>
                </Breadcrumb>
                <DtoTable
                    tableHeader={header}
                    model={DdsAssignmentRuleModel}
                    action={actions}
                    trigger={trigger}
                    allowRefreshButton={true}
                    pageSize={100}
                    scroll={{x: 1300, y: "65vh"}}/>
            </>
            {ddsAssignmentRuleDrawer.component}
        </>
    )
}
export default DdsAssignmentRulesScreen;
